import axios from 'axios';
import { JAVA_BACKEND_API_URL, JAVA_BACKEND_ORIGIN, getAuthHeader } from '../Components/Contexts/BackendConstants';
import i18n, { currentLanguageId } from "../i18n";

/**
 * Döküman şablonları — kurumun firmaya VERDİĞİ dosyalar (boş form / örnek). Backlog #251.
 * Sözleşme FE-DocumentTemplateController-guide.md'den.
 *
 * `GET /document-templates?fuarId=&languageId=` her (belge türü, dil, tür) için TEK etkin şablonu
 * döner; fuara özel satır genel satırı yener. `fuarId` verilmezse yalnızca genel (fuarsız)
 * şablonlar gelir, yani fuara özel bir şablon sessizce görünmez — firma ekranlarında hep gönder.
 * `kindKey`: 'blankForm' (indirilip imzalanıp yeniden yüklenen boş form) | 'example' (yükleme
 * kutusunun yanındaki örnek görsel). Boş şablon yoksa `[]` döner (404 değil).
 */
const BASE = `${JAVA_BACKEND_API_URL}/document-templates`;

/** document_template.language_id: 1 Türkçe (yedek dil), 2 İngilizce. */
const DIL_TR = 1;

/**
 * Şablon satırlarından (GET /document-templates ya da #212 evraklar.sablonlar) belge türü başına
 * { bosForm?: {ad, url}, ornek?: {ad, url} } (#503). Arayüz dilinin şablonu kazanır, yoksa Türkçesi; başka bir
 * dilin satırı hiç girmez. `languageId`'siz satır Türkçe sayılır. Aynı (tür, tür-adı, dil) için ilk satır kalır
 * (backend zaten yuva başına tek etkin satır döner). `kindKey` 'blankForm' | 'example' dışındaysa satır atlanır.
 * @returns {Record<number, {bosForm?: {ad: string, url: string}, ornek?: {ad: string, url: string}}>}
 */
export function sablonHaritasi(templates, { languageId = currentLanguageId() } = {}) {
  const harita = {};
  const secilenSira = {};
  for (const t of Array.isArray(templates) ? templates : []) {
    // `downloadUrl` /public/files/... köklü göreli yol; /api/v1'in DIŞINDA servis edilir.
    const yuva = t?.kindKey === 'example' ? 'ornek' : t?.kindKey === 'blankForm' ? 'bosForm' : null;
    if (!yuva || !t.downloadUrl) continue;
    const dil = t.languageId ?? DIL_TR;
    const sira = dil === languageId ? 0 : dil === DIL_TR ? 1 : null;
    if (sira == null) continue;
    const anahtar = `${t.fileTypeId}/${yuva}`;
    if (anahtar in secilenSira && secilenSira[anahtar] <= sira) continue;
    secilenSira[anahtar] = sira;
    if (!harita[t.fileTypeId]) harita[t.fileTypeId] = {};
    harita[t.fileTypeId][yuva] = { ad: t.fileGivenName || t.fileTypeName, url: `${JAVA_BACKEND_ORIGIN}${t.downloadUrl}` };
  }
  return harita;
}

/**
 * GET /document-templates?fuarId= — fuarın (ve genel) etkin şablonlarının HAM satırları, her dilde. Firma
 * evraklar uçunun sağlamadığı ekranlar (kişi sayfaları, Proje Dosyaları) bunu `sablonHaritasi`ne verir; dil
 * seçimi istemcide olur ki arayüz dili değişince yeniden çekmek gerekmesin. Hata ya da boş cevap sayfayı
 * bozmaz — bağlantılar yalnızca çıkmaz. `fuarId` verilmezse yalnızca genel (fuarsız) şablonlar gelir.
 * (WAS #251: getBosFormSablonlari — yalnızca Türkçe boş formlar.)
 */
export async function getSablonSatirlari({ fuarId } = {}) {
  try {
    const response = await axios.get(BASE, {
      params: { fuarId: fuarId || undefined },
      headers: getAuthHeader(),
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching document templates:', error);
    return [];
  }
}

// ═══════════ Yönetim tarafı — Sistem Ayarları › Belge Şablonları (backlog #259, 2026-10-11) ═══════════
//
// Yalnızca personel: GET /all, POST, PUT, DELETE #259'dan beri backend'de personel ister (önceden her
// oturum, firma girişi dahil, şablon yayımlayabiliyor ya da kaldırabiliyordu). Canlı, mock yok.
// Bir YUVA = (fileTypeId, fuarId, languageId, templateKind); yuvada tek etkin satır olur. Yeni yükleme ya da
// yeniden yayınlama yuvadaki etkin satırı yayından kaldırır, yükleme sürümü n+1 yapar.

/** template_kind: 1 boş form (indirilip imzalanıp geri yüklenen), 2 örnek (yükleme kutusunun yanında). */
export const SABLON_TURU = { BOS_FORM: 1, ORNEK: 2 };
export const SABLON_TURU_ADI = { get 1() { return i18n.t("services:documentTemplateService.bosForm"); }, get 2() { return i18n.t("services:documentTemplateService.ornek"); } };
/** DTO'nun kindKey'i → template_kind. */
export const kindKeyToTur = (k) => (k === 'example' ? SABLON_TURU.ORNEK : SABLON_TURU.BOS_FORM);
export const DIL_ADI = { 1: 'TR', 2: 'EN' };

/**
 * Hatalar iki biçimde gelir: bizim {message, result, data} zarfımız ve Spring'in varsayılanı
 * {timestamp, status, error, path} (eksik / sayısal olmayan parametre). İkisini de okur. "Bulunamadı"
 * yazma tarafında 400'dür; çok büyük dosya (sunucu sınırı) mesajsız bir 500'dür.
 */
function sablonHatasi(error, varsayilan) {
  const status = error?.response?.status ?? null;
  const data = error?.response?.data;
  let mesaj = varsayilan;
  if (status === 401) mesaj = i18n.t("services:documentTemplateService.oturumGecersiz401LutfenYeniden");
  else if (status === 403) mesaj = (data && data.message) || i18n.t("services:documentTemplateService.belgeSablonlariniYalnizcaSakaPersoneli");
  else if (status === 413 || (status === 500 && !(data && data.message))) {
    mesaj = i18n.t("services:documentTemplateService.sunucuIstegiReddettiDosyaSunucunun");
  } else if (data && typeof data === 'object' && data.message) mesaj = data.message;
  else if (data && typeof data === 'object' && data.error) mesaj = `${data.error}${data.status ? ` (${data.status})` : ''}`;
  else if (typeof data === 'string' && data.trim()) mesaj = data;
  else if (error?.message) mesaj = error.message;
  const err = new Error(mesaj);
  err.status = status;
  return err;
}

/** insertDate / updateDate 'yyyy-MM-dd HH:mm:ss.S' biçiminde (ISO değil): Date'e çevirir, olmazsa null. */
export function sablonTarihi(s) {
  if (!s) return null;
  const d = new Date(String(s).replace(' ', 'T').replace(/\.\d+$/, ''));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Şablonun indirme adresi (downloadUrl /public/files/... köklü göreli yol, /api/v1'in dışında). */
export const sablonIndirmeAdresi = (t) => (t?.downloadUrl ? `${JAVA_BACKEND_ORIGIN}${t.downloadUrl}` : null);

/**
 * GET /document-templates/all — her satır: yayından kalkmış ve eski sürümler, fuara özel olanlar dahil.
 * fuarId parametresi YOK; ekran tabloyu bir kez tümüyle çekip istemcide süzer (küçük bir tablo).
 */
export async function listAllTemplates({ fileTypeId, languageId, templateKind, onlyActive } = {}) {
  try {
    const res = await axios.get(`${BASE}/all`, {
      params: {
        fileTypeId: fileTypeId || undefined,
        languageId: languageId || undefined,
        templateKind: templateKind || undefined,
        onlyActive: onlyActive ? true : undefined,
      },
      headers: getAuthHeader(),
    });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) { throw sablonHatasi(e, i18n.t("services:documentTemplateService.sablonlarYuklenemedi")); }
}

/** GET /document-templates/file-types[?parentId=] — belge türü seçici (47 firma, 1 kişi, 2 proje, 65 stand fotoğrafı). */
export async function getTemplateFileTypes(parentId) {
  try {
    const res = await axios.get(`${BASE}/file-types`, {
      params: parentId ? { parentId } : undefined,
      headers: getAuthHeader(),
    });
    return Array.isArray(res.data) ? res.data : [];
  } catch (e) { throw sablonHatasi(e, i18n.t("services:documentTemplateService.belgeTurleriYuklenemedi")); }
}

/**
 * POST /document-templates (multipart). fuarId boş = her fuar. Yükleyen backend'de oturumdan okunur (#259).
 * Boyut ve uzantı Sistem Ayarları › Dosya Ayarları › Belge şablonları'na göre denetlenir (#259).
 * @returns yayımlanan satır (DocumentTemplateDTO)
 */
export async function publishTemplate({ file, fileTypeId, fuarId, languageId = 1, templateKind = SABLON_TURU.BOS_FORM }) {
  const form = new FormData();
  form.append('file', file);
  form.append('fileTypeId', String(fileTypeId));
  if (fuarId) form.append('fuarId', String(fuarId));
  form.append('languageId', String(languageId));
  form.append('templateKind', String(templateKind));
  try {
    const res = await axios.post(BASE, form, { headers: getAuthHeader() });
    if (res.data && res.data.result === false) throw new Error(res.data.message || i18n.t("services:documentTemplateService.sablonYayimlanamadi"));
    return res.data?.data ?? null;
  } catch (e) {
    if (!e.response) throw e;
    throw sablonHatasi(e, i18n.t("services:documentTemplateService.sablonYayimlanamadi"));
  }
}

/**
 * PUT /document-templates/{id} (JSON, kısmi). fileGivenName yalnızca görünen ad; isActive false yayından
 * kaldırır, true yeniden yayınlar (yuvadaki etkin satırı kaldırarak). Dosyanın kendisi değişmez.
 */
export async function updateTemplate(id, degisiklik) {
  try {
    const res = await axios.put(`${BASE}/${id}`, degisiklik, { headers: getAuthHeader() });
    return res.data?.data ?? null;
  } catch (e) { throw sablonHatasi(e, i18n.t("services:documentTemplateService.sablonGuncellenemedi")); }
}

/**
 * DELETE /document-templates/{id} — YUMUŞAK: yalnızca yayından kaldırır, hiçbir şey silinmez ve geri
 * alınabilir. Dosya adresiyle indirilmeye devam eder; toplanmış imzalı kopyalara dokunmaz.
 */
export async function retireTemplate(id) {
  try {
    const res = await axios.delete(`${BASE}/${id}`, { headers: getAuthHeader() });
    return res.data;
  } catch (e) { throw sablonHatasi(e, i18n.t("services:documentTemplateService.sablonYayindanKaldirilamadi")); }
}
